import Component from '@glimmer/component'
import { isPresent } from '@ember/utils'
import { service } from '@ember/service'
import { trackedFunction } from 'reactiveweb/function'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import type { IntlService } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import type { FormatValueOptions } from 'teacher-ui-client/utils/format-value'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { hash } from '@ember/helper'
import { and, not } from 'ember-truth-helpers'
import type { PeaksProgressData } from 'teacher-ui-client/utils/student-events/fetchers/peaks-progress'

interface Args {
  isLoadingItems?: boolean
  sortingConfig?: SortingConfig
  teachers?: TeacherItem[]
  exportable?: boolean
  onSortClick?: (sortObject: SortObject) => unknown
}

interface Signature {
  Args: Args
}

export interface TeacherItem {
  id: string
  firstName: string
  lastName: string
  grades: string
  studentsSummary: PeaksProgressData['summary']
}

interface Column {
  value?: string
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell
  tooltipValue?: string
  formatEmptyConfig?: FormatValueOptions
}

export class TablesPeaksProgressTeachersComponent extends Component<Signature> {
  @service intl!: IntlService

  @service dataTransformer!: DataTransformerService

  get isUpdating() {
    return this.args.isLoadingItems || this.transformedItems.isPending
  }

  /**
   * Returns an array of operation descriptions that will be applied to the items
   */
  get pipelineDescriptors(): Nullable<PipelineDescriptor>[] {
    return [this.sortingDescriptor]
  }

  get sortingDescriptor() {
    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(this.columns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort' as const, config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  transformedItems = trackedFunction(this, async (): Promise<TeacherItem[]> => {
    const presentDescriptors = this.pipelineDescriptors.filter(isPresent)
    const transformedData = await this.dataTransformer.buildAndTransform<TeacherItem, TeacherItem>(
      [...(this.args.teachers ?? [])], // spread to entangle with each item in the tracked array as they are added
      presentDescriptors,
    )
    return transformedData.items
  })

  get teacherRows() {
    return this.transformedItems.value ?? []
  }

  get columns(): Column[] {
    const { intl } = this
    const translationPrefix = 'tables.peaksProgress.teachers.headers'
    const translationTooltipPrefix = 'tables.peaksProgress.teachers.tooltips'

    const defaultColumn: Column = {
      classNames: 'table-box table-box-header bg-oceany-blue-100',
      cellClassNames: 'table-box table-box-cell bg-oceany-blue-50 group-hover:bg-oceany-blue-100',
      component: SortableCell,
      comparerKey: 'sortByNumber',
    }

    const columns: Column[] = [
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.firstName`),
        valuePath: 'firstName',
        comparerKey: 'sortByValue',
        cellClassNames: `${defaultColumn.cellClassNames} print:wrap-break-word`,
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.lastName`),
        valuePath: 'lastName',
        comparerKey: 'sortByValue',
        cellClassNames: `${defaultColumn.cellClassNames} print:wrap-break-word`,
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.grades`),
        valuePath: 'grades',
        comparerKey: 'sortByValue', // would normally be sortByGrade, but this will be multiple grades.
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.peaksCompleted`),
        valuePath: 'studentsSummary.lessons.lessons_completed',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.soundGemsEarned`),
        valuePath: 'studentsSummary.sound_gems.sound_gems_earned',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.wordGemsEarned`),
        valuePath: 'studentsSummary.word_gems.word_gems_earned',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.booksRead`),
        valuePath: 'studentsSummary.book_quizzes.books_read',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.averageQuizScore`),
        valuePath: 'studentsSummary.lesson_quizzes.student_average',
        formatEmptyConfig: { append: '%' },
        tooltipValue: intl.t(`${translationTooltipPrefix}.averageQuizScore`),
      },
    ]

    return columns
  }

  get noDataMessage() {
    return this.intl.t('tables.peaksProgress.emptyTableMessage')
  }

  <template>
    <div>
      <BasicTable
        class="relative w-full"
        exportable={{@exportable}}
        @columns={{this.columns}}
        @rows={{this.teacherRows}}
        @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
        as |bt|
      >
        <bt.head />

        {{! If not transforming and no teachers to show, always only display the empty message }}
        {{#if (and (not this.isUpdating) (not this.teacherRows))}}
          <bt.spanner class="p-3 text-center italic">
            {{this.noDataMessage}}
          </bt.spanner>
        {{else}}
          {{! If transforming, always show a loading spinner}}
          {{! When no items, show it normally. }}
          {{! If there are items, place it on top of the existing table body (absolute) }}
          {{#if this.isUpdating}}
            <bt.spanner
              class="table-box table-box-cell border-transparent
                {{if this.teacherRows 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </bt.spanner>
          {{/if}}
          <bt.body class={{if this.isUpdating "opacity-50"}} as |b|>
            <b.row class="table-row-striped group" />
          </bt.body>
        {{/if}}

      </BasicTable>
    </div>
  </template>
}

export default TablesPeaksProgressTeachersComponent
